import type { Maybe } from '@adornis/base/utilTypes.js';
import type { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import type { Action } from '@adornis/buchungsheld-components/BuchungsheldLitElement.js';
import type { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-loader.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import { fullEditorConfig } from '@adornis/html-based-buildify/client/editor/presets/full-editor.js';
import '@adornis/html-based-buildify/client/editor/x-editor.js';
import type { IMarkConfig, INodeConfig } from '@adornis/html-based-buildify/client/types.js';
import { clientTranslator } from '@adornis/translation-core/client/clientTranslator.js';
import { html } from 'lit';

export function bhBuldifyEditor(
  codeParentEntity: Maybe<AdornisEntity>,
  buildifyCodeField: string,
  config: {
    prosemirrorConfig?: Array<INodeConfig | IMarkConfig>;
    breakpoints?: Record<string, number>;
    defaultDesign?: string; // buildify design preset
  } = {},
) {
  if (!codeParentEntity) return html` <x-loader></x-loader> `;

  return html`
    <x-editor
      .configs=${config.prosemirrorConfig ?? fullEditorConfig()}
      .value=${codeParentEntity[buildifyCodeField] ?? config.defaultDesign ?? ''}
      .sizeBreakpoints="${config.breakpoints ?? []}"
      .globalSettingsClassName="BuildifyGlobalSettings"
      @value-picked=${async e => {
        if (e.detail.value === undefined) return;
        codeParentEntity[buildifyCodeField] = e.detail.value;
      }}
    ></x-editor>
  `;
}

export function bhBuldifyEditorActions(
  host: ChemistryLitElement,
  codeParentEntity: Maybe<AdornisEntity>,
  buildifyCodeField: string,
  defaultDesign?: string,
) {
  if (!codeParentEntity) return [];

  return [
    {
      icon: 'save',
      callback: () => saveDesign(host, codeParentEntity, buildifyCodeField),
      text: 'Design speichern',
    },
    {
      icon: 'delete',
      callback: () => deleteDesign(host, codeParentEntity, buildifyCodeField, defaultDesign),
      color: 'error',
      text: null,
    },
  ] as Action[];
}

async function saveDesign(host: ChemistryLitElement, codeParentEntity: AdornisEntity, buildifyCodeField: string) {
  await XDialog.waitFor(codeParentEntity.save());
  const successMessage = clientTranslator.translate('actions', 'that_worked');
  await XSnackbar.show(successMessage);
  host.requestUpdate();
}

async function deleteDesign(
  host: ChemistryLitElement,
  codeParentEntity: AdornisEntity,
  buildifyCodeField: string,
  defaultDesign?: string,
) {
  const doIT = await XDialog.confirm(
    html`
      <x-flex space="md">
        <x-h3 ${css({ alignSelf: 'center' })}> Achtung! </x-h3>
        <x-text>
          Willst du dein eigen erstelltes Design wirklich löschen? Dies kann
          <b>nicht rückgängig</b> gemacht werden
        </x-text>
      </x-flex>
    `,
    {
      highlightDecline: true,
      acceptText: 'Löschen',
      declineText: 'Abbrechen',
    },
  );

  if (doIT) {
    codeParentEntity[buildifyCodeField] = defaultDesign ?? '';
    await XDialog.waitFor(codeParentEntity.save());
    host.requestUpdate();
  }
}
